<template>
  <v-dialog v-model="dialogValue" max-width="600px" lazy>
    <v-card>
      <alert-banner :msg="alertMessage" />
      <v-form @submit.prevent="saveUser">
        <v-card-title>
          <span class="headline">
            Edit User
          </span>
          <v-spacer />
          <div class="text-xs-right">
            <v-switch v-model="editForm.active" label="Active" />
          </div>
        </v-card-title>
        <v-card-text>
          <v-container grid-list-md>
              <v-layout wrap row justify-center>
                  <v-flex xs11>
                      <v-text-field v-model="editForm.firstName" v-validate="'required'" name="firstName" label="First Name*" required :error-messages="errors.collect('firstName')" />
                  </v-flex>
                  <v-flex xs11>
                      <v-text-field v-model="editForm.lastName" v-validate="'required'" name="lastName" label="Last Name*" required :error-messages="errors.collect('lastName')" />
                  </v-flex>
                  <v-flex xs11>
                      <v-text-field v-model="editForm.email" v-validate="'required'" type="email" name="email" label="Email*" required :error-messages="errors.collect('email')" />
                  </v-flex>
                  <v-flex xs11>
                      <v-checkbox v-model="editForm.emailConfirmed"  type="email" name="emailConfirmed" label="Email Confirmed" :error-messages="errors.collect('emailConfirmed')" />
                  </v-flex>
                  <v-flex xs11>
                      <v-text-field v-model="editForm.newPassword" type="password" name="password" label="New Password" hint="If new password is supplied, it is automatically expired on user login." :error-messages="errors.collect('password')" />
                  </v-flex>
                  <v-flex xs11>
                      <label>Enter a short bio</label>
                      <vue-editor v-model="editForm.bio" :editor-toolbar="customToolbar" />
                  </v-flex>
                  <v-flex xs11>
                      <v-select v-model="editForm.programIds" v-validate="'required'" multiple :items="programs" label="What programs do you work with?*" name="programIds" :error-messages="errors.collect('programIds')" required item-text="name" item-value="id" id="programSelect" />
                  </v-flex>
                  <v-flex xs11>
                      <v-select v-model="editForm.positionTypeId" :items="positions" label="Position" name="positionTypeId" :error-messages="errors.collect('positionTypeId')" item-text="name" item-value="id" />
                  </v-flex>
                  <v-flex v-if="editForm.positionTypeId === otherPositionId" xs11>
                      <v-text-field v-model="editForm.otherPositionTypeDetail" label="Other Position (Please Specify)" name="otherPositionTypeDetail" :error-messages="errors.collect('otherPositionTypeDetail')" />
                  </v-flex>
                  <v-flex xs11>
                      <v-autocomplete v-model="editForm.institutionId" v-validate="'required'" :items="institutions" class="mx-1" hide-details label="Institution Name*" name="institutionId" :error-messages="errors.collect('institutionId')" required item-text="name" item-value="id" @change="editForm.campusId = null" />
                  </v-flex>
                  <v-flex v-if="editForm.institutionId" xs11>
                      <v-autocomplete v-model="editForm.campusId" v-validate="'required'" :items="campuses" class="mx-1" hide-details label="Campus Name*" name="campusId" :error-messages="errors.collect('campusId')" required item-text="name" item-value="id" />
                  </v-flex>
                  <v-flex xs11>
                      <v-select v-model="editForm.roles" v-validate="'required'" :items="availableRoles" label="Select a Role(s)" name="roles" multiple :error-messages="errors.collect('roles')" />
                  </v-flex>
              </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <span class="pl-4">
            <small>*indicates required field</small>
          </span>
          <v-spacer />
          <v-btn flat @click="dialogValue = false">
            Close
          </v-btn>
          <v-btn type="submit" color="secondary darken-1" flat :loading="saving">
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
  import axios from 'axios';

  export default {
    props: {
      dialog: {
        type: Boolean,
        default: false,
      },

      user: {
        type: Object,
        default: () => {},
      },

      availableRoles: {
        type: Array,
        default: () => {},
      },
    },

    data: () => ({
      editForm: {
        bio: '',
        firstName: '',
        lastName: '',
        email: '',
        newPassword: '',
        positionTypeId: '',
        campusId: null,
        institutionId: null,
        otherPositionTypeDetail: '',
        roles: [],
        programIds: [],
      },
      customToolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
      ],
      institutions: [],
      campuses: [],
      positions: [],
      authors: [],
      saving: false,
      programs: [],
    }),

    computed: {
      dialogValue: {
        get() {
          return this.dialog;
        },
        set(value) {
          this.$emit('update:dialog', value);
        },
      },

      otherPositionId() {
        const positions = JSON.parse(JSON.stringify(this.positions));
        const position = positions.find(x => x.name.includes('Other'));
        if (position) return position.id;

        return -1;
      },
    },

    watch: {
      dialogValue(val) {
        if (!val) {
          setTimeout(() => {
            this.clearErrors();
          }, 300);
        } else {
          this.editForm = this.user;
          this.setSelectedUserPrograms();
          this.$validator.pause();
        }
      },

      'editForm.institutionId': function () {
        this.getCampuses();
      },
    },
    mounted() {
      this.getPositionTypes();
      this.getInstitutions();
      this.getPrograms();
    },

    methods: {
      async getInstitutions() {
        const { data } = await axios.get('/api/institutions');
        this.institutions = data;
      },

      async getCampuses() {
        const { data } = await axios.get(`/api/institutions/${this.editForm.institutionId}/campuses`);
        this.campuses = data;
      },

      async saveUser() {
        if (this.editForm.positionTypeId != this.otherPositionId) {
          this.editForm.otherPositionTypeDetail = '';
        }
        const result = await this.startFormProcessing();
        if (!result) return;
        this.saving = true;
        try {
          await axios.patch(`/api/users/${this.user.id}`, this.editForm);
          this.$emit('getUsers');
          this.dialogValue = false;
        } catch (error) {
          this.processErrors(error, this.editForm);
        } finally {
          this.saving = false;
        }
      },

      async getPositionTypes() {
        const { data } = await axios.get('/api/registration/position-types');
        this.positions = data;
      },

       async getPrograms() {
                const { data } = await axios.get('/api/programs');
                this.programs = data;
        },

        async setSelectedUserPrograms() {
            const userPrograms = this.editForm.programs;
            const allPrograms = this.programs;
            this.editForm.programIds = [];
            const existingProgramIds = [];
            console.log("Out for each");

            allPrograms.forEach(function (program) {
                console.log("In for each");
                if (userPrograms.indexOf(program.name) !== -1) {
                    console.log("In if statement");
                    existingProgramIds.push(program.id);
                }
            });

            this.editForm.programIds = existingProgramIds;
        }

    },
  };
  </script>